import { FC, memo, useMemo } from 'react'
import { Link } from 'react-router'
import r from 'routes'

import { AppInfo } from '@/apps'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import useAvailableApps from '@/hooks/useAvailableApps'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store'

const AppSelector: FC = () => {
  const { app } = useAppSelector()
  const apps = useAvailableApps()
  const activeApp = useMemo(() => apps.find(a => a.id === app), [app, apps])

  return (
    <NavigationMenu delayDuration={0}>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className="justify-start w-60 pl-3 [&>svg:last-child]:ml-auto bg-transparent cursor-default border"
            onClick={e => e.preventDefault()}
          >
            {activeApp ? (
              <>
                {<activeApp.icon className="w-5 h-5 mr-2" />}
                {activeApp.title}
              </>
            ) : (
              'Select an app'
            )}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="w-60">
              {apps.map(app => (
                <ListItem key={app.id} app={app} isCurrent={activeApp?.id === app.id}></ListItem>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  )
}

type ItemProps = {
  app: AppInfo
  isCurrent?: boolean
  className?: string
}

const ListItem: FC<ItemProps> = ({ app, isCurrent, className }) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={r.apps[app.id].root}
          className={cn(
            'block select-none space-y-1 first-child:rounded-t-md last-child:rounded-b-md px-3 py-2 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
            { 'opacity-50 pointer-events-none': isCurrent },
            className
          )}
        >
          <div className="flex items-center text-foreground">
            <app.icon className="w-6 h-6 mr-2" />
            <span className="text-sm">{app.title}</span>
          </div>
        </Link>
      </NavigationMenuLink>
    </li>
  )
}

export default memo(AppSelector)
